import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useContext, watch } from '@nuxtjs/composition-api';
import 'lazysizes';
import ProfileSelector from '@/components/partials/optional-anonymity/ProfileSelector/ProfileSelector.vue';
import { getActiveStorageUrl } from '@/components/partials/war-room/WarRoomTextEditor/utils';
import AddImageSvg from '@/components/partials/war-room/WarRoomCommentCreateForm/images/add-image.vue.svg';
import RemoveImageSvg from '@/components/partials/war-room/WarRoomCommentCreateForm/images/remove-image.vue.svg';
import uploader from '@/components/utils/uploader';
import ProfileSelectorAvatar from '@/components/partials/optional-anonymity/ProfileSelector/ProfileSelectorAvatar.vue';
import type { Profile } from '@/components/partials/optional-anonymity/ProfileSelector/types';
import { MENTIONS_CONTAINER_CLASS } from '@/components/common/BravadoEditor/constants';
import BravadoProgressChartV2 from '~/components/common/BravadoProgressChartV2/BravadoProgressChartV2.vue';

type WarRoomCommentCreateFormEditorMode = 'create' | 'edit';
type WarRoomCommentCreateFormEditorProps = {
  imageKey?: string | null;
  isAnonymous?: boolean | null;
  mode?: WarRoomCommentCreateFormEditorMode;
  initialText?: string;
  boostUsernames?: Array<string>;
  autofocus?: boolean;
};
type WarRoomCommentCreateFormEditorEmits = {
  (event: 'image-uploading', payload: boolean): void;
  (event: 'text-change', payload: string): void;
  (event: 'image-change', payload: { media: string; key: string }): void;
  (event: 'is-anonymous-change', payload: { isAnonymous: boolean; loaded: boolean }): void;
  (event: 'image-remove' | 'post-shortcut-entered'): void;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'WarRoomCommentCreateFormEditor',
  props: {
    imageKey: { default: null },
    isAnonymous: { type: [Boolean, null], default: null },
    mode: { default: 'create' },
    initialText: { default: '' },
    boostUsernames: { default: () => [] },
    autofocus: { type: Boolean, default: false }
  },
  emits: ["image-uploading", "text-change", "image-change", "is-anonymous-change", "image-remove", "post-shortcut-entered"],
  setup(__props: any, { expose, emit }: { emit: ({
  (event: 'image-uploading', payload: boolean): void;
  (event: 'text-change', payload: string): void;
  (event: 'image-change', payload: { media: string; key: string }): void;
  (event: 'is-anonymous-change', payload: { isAnonymous: boolean; loaded: boolean }): void;
  (event: 'image-remove' | 'post-shortcut-entered'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { imageKey: string | null, isAnonymous: boolean | null, mode: WarRoomCommentCreateFormEditorMode, initialText: string, boostUsernames: Array<string>, autofocus: boolean };




const { $analytics, $notice, $sentryUtils, $auth } = useContext();

// Set contenteditable false for all mentions to keep delete full mention functionality by one key from tributejs
const interactMentiones = (string: string) => {
  return string.replace(/<a data-mention-user-username/gim, '<a contenteditable="false" data-mention-user-username');
};

const FILE_SIZE_LIMIT = 5;

const text = ref('');
const focusIn = ref(false);
const uploadedImageUrl = ref<string | null>(null);
const isImageUploading = ref(false);
const imageUploadingId = ref<number | null>(null);
const uploadingProgress = ref(0);
const profile = ref<Profile | null>(null);
const textInput = ref<HTMLElement | null>(null);

const isPlaceholderVisible = computed(() => !(text.value.replace(/^<br>/g, '').trim().length || focusIn.value));
const imageUrl = computed(() => uploadedImageUrl.value || (props.imageKey && getActiveStorageUrl(props.imageKey)));
const isEdit = computed(() => props.mode === 'edit');

function focusInput() {
  nextTick(() => {
    textInput.value?.focus();
  });
}

watch(
  () => props.autofocus,
  newVal => {
    if (newVal) focusInput();
  },
  { immediate: true },
);
watch(isImageUploading, newVal => {
  emit('image-uploading', newVal);
});

function onRemoveClick() {
  isImageUploading.value = false;

  imageUploadingId.value = null;
  uploadedImageUrl.value = null;

  emit('image-remove');
}

function clearEditor() {
  text.value = '';
  if (textInput.value) textInput.value.innerHTML = '';
  emit('text-change', '');

  onRemoveClick();
}

function setHTML(html: string) {
  if (textInput.value) {
    text.value = textInput.value.innerHTML = interactMentiones(html);
    emit('text-change', text.value);
  }
}

function trubuteReplacedhandler() {
  text.value = textInput.value?.innerHTML || '';
}

onMounted(() => {
  if (props.initialText) {
    setHTML(props.initialText);
    focusInput();
  }

  textInput.value?.addEventListener('tribute-replaced', trubuteReplacedhandler);
});

onBeforeUnmount(() => {
  textInput.value?.removeEventListener('tribute-replaced', trubuteReplacedhandler);
});

function handlePaste(e: ClipboardEvent) {
  // trying to paste image
  if (e.clipboardData?.items) {
    const items = e.clipboardData.items;

    for (let i = 0; i < items.length; i++) {
      if (!items[i].type.includes('image')) continue;

      onImageChange({ target: { files: [items[i].getAsFile()] } });

      e.preventDefault();
      e.stopPropagation();

      return;
    }
  }

  // if not image then trying to paste html/text
  const html = e.clipboardData?.getData('text/html');

  if (!html) return;

  e.preventDefault();
  e.stopPropagation();

  const text = html.replace(/(style="([^"]*)")/gi, '');
  document.execCommand('insertHTML', false, text);
}

function handleInput(e: { target: HTMLInputElement }) {
  text.value = e.target.innerHTML;
  emit('text-change', text.value);
}

function handleFocus() {
  focusIn.value = true;
}

function handleBlur() {
  focusIn.value = false;
}

function handleKeyDown(e: KeyboardEvent) {
  const isEnter = e.key === 'Enter';
  const isFnKey = e.keyCode === 10;
  const mentionsDropdown = document.querySelector(`.${MENTIONS_CONTAINER_CLASS}`);
  const isMentionsVisible = !mentionsDropdown?.getAttribute('style')?.match(/none/);

  // War Room user mentions opened - skip enter to select user
  if (isMentionsVisible && isEnter) {
    return;
  }
  // Enter - insert new line
  if (isEnter && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    document.execCommand('insertLineBreak');
    return;
  }
  // Ctrl + Enter/Fn - submit
  if ((e.ctrlKey || e.metaKey) && (isEnter || isFnKey)) {
    emit('post-shortcut-entered');
  }
}

function addImageClick() {
  $analytics?.logEvent('war-room__post__comment-add-image__click');
}

function onImageChange(event: { target: { value?: string; files: Array<File | null> } }) {
  const file = event.target.files[0];

  if (!file || !file.type.includes('image')) {
    return;
  }

  if (getSizeInMb(file.size) > FILE_SIZE_LIMIT) {
    $notice.error(`File should be less than ${FILE_SIZE_LIMIT}mb!`);
    event.target.value = '';
    return;
  }

  uploadFile(file);
}

async function uploadFile(file: File) {
  try {
    const now = Date.now();

    isImageUploading.value = true;
    imageUploadingId.value = now;

    uploadingProgress.value = 0;

    uploadedImageUrl.value = URL.createObjectURL(file);
    const metadata = await uploader.upload({
      file,
      token: $auth.user?.token ?? '',
      onProgress: progress => {
        if (imageUploadingId.value === now) {
          uploadingProgress.value = progress * 100;
        }
      },
    });

    if (imageUploadingId.value !== now) {
      return;
    }

    emit('image-change', { media: metadata.signed_id, key: metadata.key });

    isImageUploading.value = false;
  } catch (e) {
    uploadedImageUrl.value = null;
    imageUploadingId.value = null;

    isImageUploading.value = false;

    $notice.error('Failed to upload image.');
    $sentryUtils.error(e);
  }
}

function getSizeInMb(bytes: number) {
  return parseFloat((bytes / 1024 ** 2).toFixed(2));
}

function handleProfileChange({ isAnonymous, profile: userProfile }: { isAnonymous: boolean; profile: Profile }) {
  profile.value = userProfile;

  emit('is-anonymous-change', { isAnonymous, loaded: !!userProfile.slug });
}

expose({
  clearEditor,
  setHTML,
});

return { __sfc: true,props, emit, $analytics, $notice, $sentryUtils, $auth, interactMentiones, FILE_SIZE_LIMIT, text, focusIn, uploadedImageUrl, isImageUploading, imageUploadingId, uploadingProgress, profile, textInput, isPlaceholderVisible, imageUrl, isEdit, focusInput, onRemoveClick, clearEditor, setHTML, trubuteReplacedhandler, handlePaste, handleInput, handleFocus, handleBlur, handleKeyDown, addImageClick, onImageChange, uploadFile, getSizeInMb, handleProfileChange, ProfileSelector, AddImageSvg, RemoveImageSvg, ProfileSelectorAvatar, BravadoProgressChartV2 }
}

})